<template lang="pug">
.invoice-logo-uploader
  .form-group
    .label-with-tooltip
      label.control-label Logo
      i.hi.hi-info.ml-5(
        v-tooltip
        data-original-title="You can save an invoice logo for all invoices, or use your default organization logo."
      )
    .change-invoice-logo(
      v-if="showChangeLogo"
      @click="showAddLogoDialog"
    )
      | Change invoice logo
  .upload-zone(
    @drop.prevent="addFile"
    @dragover.prevent
  )
    AppImageUploader(
      :file-url="fileUrl"
      :loading="false"
      :placeholder="false"
      :show-label="!logoAttached"
      :accepted-file-types="acceptedFileTypes"
      @start-uploading="onStartUploading"
    )
      template(slot="uploadText")
        div
          | Drag and drop your image here or
          span.text-blue  browse files
  .action-buttons
    i.hi.hi-12.hi-delete-xs.clickable(
      v-if="logoAttached"
      @click="onStartRemoving"
    )

  input#invoice_logo(
    :value="paramsForInvoiceLogo"
    type="hidden"
    name="invoice[logo]"
  )

  AppDialog.add-logo(
    append-to-body
    size="medium"
    :show="showAddLogo"
    title="Add invoice logo"
    @close="cancelAddLogo"
  )
    p This logo will be used for all future invoices created. You can change or remove it later from your invoice settings.
    AppImageUploader(
      :file-url="organization.invoice_logo"
      :loading="false"
      :placeholder="false"
      :accepted-file-types="acceptedFileTypes"
      @start-uploading="uploadFromAddDialog"
    )
    template(slot="footer")
      AppButton.cancel-add-logo(@click="cancelAddLogo")
        | Cancel
      AppButton.btn-primary.submit-add-logo(@click="saveAddLogo")
        | Save

  AppDialog.apply-logo(
    append-to-body
    size="medium"
    :show="showApplyToAll"
    title="Apply logo to all invoices?"
    @close="applyToAllDialogClose"
  )
    p Do you want to save this as your invoice logo and apply it to all of your future invoices?
    p You can change or remove it later from your invoice settings.
    template(slot="footer")
      AppButton.apply-to-this(@click="applyToThisInvoice")
        | Just this invoice
      AppButton.btn-primary.apply-to-all(@click="applyToAllInvoices")
        | Apply to all

  AppDialog.remove-logo(
    append-to-body
    size="medium"
    :show="showRemoveFromAll"
    title="Remove logo from all invoices?"
    @close="removeFromAllDialogClose"
  )
    p Do you want to remove this from all of your future invoices?
    p You can change or remove it later from your invoice settings.
    template(slot="footer")
      AppButton.remove-from-this(@click="removeFromThisInvoice")
        | Just this invoice
      AppButton.btn-primary.remove-from-all(@click="removeFromAllInvoices")
        | Apply to all
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { mapFormFields } from '~/utils/form_utils';
import { SET_LOADING, UPDATE_ORGANIZATION_LOGO, UPDATE_INVOICE_LOGO } from '../../stores/InvoicesStore/types';

export default {
  name: 'LogoUploader',
  props: {},
  data() {
    return {
      showApplyToAll: false,
      fileData: null,
      invoiceLogoParams: null,
      showRemoveFromAll: false,
      showAddLogo: false,
    };
  },
  computed: {
    ...mapFormFields(
      'organization',
      {
        organizationInvoiceLogo: 'invoice_logo',
      },
      UPDATE_ORGANIZATION_LOGO,
    ),
    ...mapFormFields(
      'invoice',
      {
        invoiceLogo: 'logo',
      },
      UPDATE_INVOICE_LOGO,
    ),
    ...mapGetters(['showUploadLoading', 'hasInvoiceLogo', 'hasLogoRemoved']),
    ...mapState({
      organization: 'organization',
      invoice: 'invoice',
      logoParams: 'logo_params',
      errorLogoParams: 'error_logo_params',
    }),
    fileUrl() {
      const logoParams = this.invoiceLogoParams;

      if (logoParams === 'use-default' && !this.showAddLogo) {
        // Setting a default invoice logo
        return this.organization.invoice_logo;
      }
      if (this.paramsForInvoiceLogo === '{}') {
        // Removing the logo from an invoice
        return null;
      }
      if (!this.hasLogoRemoved && (logoParams === null || logoParams.id)) {
        // Ok to show a logo if one is present
        return this.invoice.logo || this.organization.invoice_logo || this.organization.logo;
      }
      if (this.hasLogoRemoved && logoParams && logoParams.id) {
        // Logo was removed but is getting added back
        return this.invoice.logo || this.organization.invoice_logo;
      }
      if (this.hasLogoRemoved) {
        // Logo was explicitly removed, show none
        return null;
      }

      // Fallback (will either be the logo or null)
      return this.organization.logo;
    },
    paramsForInvoiceLogo() {
      const logoParams = this.invoiceLogoParams;

      if (logoParams === 'use-default') {
        // Using the default organization invoice logo
        return logoParams;
      }
      if (logoParams) {
        // Uploading a new logo (or setting a blank one)
        return JSON.stringify(logoParams);
      }
      if (this.errorLogoParams) {
        // Backup in case there was an error with the form submission and a logo had been added already
        return JSON.stringify(this.errorLogoParams);
      }

      return null;
    },
    logoAttached() {
      const logoRemoved = (this.hasLogoRemoved && !this.invoiceLogoParams) || this.paramsForInvoiceLogo === '{}';

      return (this.hasInvoiceLogo || this.organization.logo) && !logoRemoved;
    },
    acceptedFileTypes() {
      return ['.jpeg', '.jpg', '.gif', '.png'];
    },
    showChangeLogo() {
      return (
        this.showAddLogo ||
        (!this.hasLogoRemoved && this.paramsForInvoiceLogo !== '{}' && !this.hasInvoiceLogo && this.organization.logo)
      );
    },
  },
  methods: {
    ...mapActions(['uploadFile', 'deleteFile', 'uploadOrganizationInvoiceLogo']),
    ...mapMutations({
      setLoading: SET_LOADING,
    }),
    addFile(event) {
      const droppedFiles = event.dataTransfer.files;

      if (droppedFiles && !this.logoAttached) {
        if (droppedFiles.length === 1) {
          this.onStartUploading(droppedFiles[0]);
        } else {
          this.showMessage('Please drag only one file.');
        }
      }
    },
    uploadFromAddDialog(file) {
      this.setLoading(true);
      this.fileData = file;
      this.applyToAllInvoices();
    },
    onStartUploading(file) {
      this.setLoading(true);
      this.fileData = file;
      this.showApplyToAll = true;
    },
    onStartRemoving() {
      if (this.organization.invoice_logo && !this.invoice.logo) {
        this.showRemoveFromAll = true;
      } else {
        this.removeFromThisInvoice();
      }
    },
    applyToThisInvoice() {
      this.uploadFile(this.fileData)
        .then((response) => {
          this.invoiceLogo = response.get_url;
          this.invoiceLogoParams = this.logoParams;
        })
        .catch((e) => {
          this.setLoading(false);
          this.$appMessage(e.message);
        });
      this.fileData = null;
      this.showApplyToAll = false;
    },
    applyToAllInvoices() {
      this.uploadFile(this.fileData)
        .then((response) => {
          this.organizationInvoiceLogo = response.get_url;
          this.uploadOrganizationInvoiceLogo();
        })
        .catch((e) => {
          this.setLoading(false);
          this.$appMessage(e.message);
        });
      this.fileData = null;
      this.invoiceLogoParams = 'use-default';
      this.showApplyToAll = false;
    },
    removeFromThisInvoice() {
      this.invoiceLogoParams = {};
      this.showRemoveFromAll = false;
    },
    removeFromAllInvoices() {
      this.organizationInvoiceLogo = null;
      this.deleteFile();
      if (!this.invoice.id) {
        // If this is a new invoice, we don't need to pass any logo params after removing a logo
        this.invoiceLogoParams = null;
      }
      this.showRemoveFromAll = false;
    },
    applyToAllDialogClose() {
      this.showApplyToAll = false;
    },
    removeFromAllDialogClose() {
      this.showRemoveFromAll = false;
    },
    showAddLogoDialog() {
      this.showAddLogo = true;
    },
    cancelAddLogo() {
      this.removeFromAllInvoices();
      this.invoiceLogoParams = null;
      this.showAddLogo = false;
    },
    saveAddLogo() {
      this.showAddLogo = false;
    },
  },
};
</script>
